import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './Button';
import { Link as LinkIcon, CheckCircle2, X, ArrowRightLeft, AlertTriangle, Ban } from 'lucide-react';
import { countLinkedChildren, loadProjectById, loadProjectsFromCloud } from '../services/cloudService';
import { isChildProject, linkTransferPlanFingerprint, planLinkTransfer, type LinkTransferPlan } from '../services/projectLv';
import { ReportMetadata, ServiceItem } from '../types';
import type { ListProject } from '../services/listProject';
import type { MutterProjekt } from '../hooks/useProjectLinking';
import { anzeigeNameAktuellesProjekt, passtZurMutterSuche, type VerknuepfungsProjekt } from './projectLinkingLogic';

/**
 * Das Projekt, das Tochter werden soll — KEIN Listeneintrag: ProjectHub
 * reicht `{ id, data: report }` herein, also den geoeffneten Bericht.
 * ProjectHub setzt `name` nicht; den angezeigten Namen bestimmt deshalb
 * `anzeigeNameAktuellesProjekt` (Rueckfall auf `metadata.projectName`).
 */
type AktuellesProjekt = VerknuepfungsProjekt;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    currentProject: AktuellesProjekt;
    onLink: (parentProject: MutterProjekt, mode: 'daily_flat' | 'individual', plan: LinkTransferPlan) => Promise<void> | void;
    /**
     * Dieselbe Regel wie in ProjectHub (`canSeeFinancials` = admin |
     * super_admin). Betrifft nur den Grund „abweichender Einheitspreis": ohne
     * das Recht wird die Kollision genannt, nicht ihr Betrag. Der Nutzer
     * braucht die Zahl hier nicht — er muss nur wissen, DASS die Position
     * eigenstaendig wandert.
     *
     * Vorgabe FALSE: eine vergessene Aufrufstelle verschweigt den Preis.
     */
    canSeeFinancials?: boolean;
}

const formatAmount = (value: number): string => value.toLocaleString('de-DE');

const formatPrice = (value: number): string =>
    (value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/** "2.7.50 DGUV-Begehung" — ohne LV-Nummer nur der Titel. */
const serviceLabel = (service: ServiceItem): string =>
    [service.lvPosition, service.title].filter(Boolean).join(' ') || 'Position ohne Bezeichnung';

const servicesOf = (project: { data?: { metadata?: ReportMetadata } } | null | undefined): ServiceItem[] =>
    project?.data?.metadata?.globalData?.services || [];

/**
 * Ein Suchtreffer samt der Frage, ob er ueberhaupt Mutter werden darf.
 *
 * Nicht waehlbare Treffer werden BEWUSST angezeigt statt weggefiltert: wer
 * die Projektnummer vor sich hat und sie in der Liste nicht findet, sucht
 * weiter, tippt sie neu und glaubt am Ende an einen Fehler der Suche. Der
 * Grund gehoert neben den Treffer.
 */
interface Candidate {
    project: ListProject;
    /** Gesetzt, wenn dieses Projekt nicht als Mutter taugt — der Text steht in der Liste. */
    blockedReason?: string;
}

export const ProjectLinkingModal: React.FC<Props> = ({ isOpen, onClose, currentProject, onLink, canSeeFinancials = false }) => {
    const [search, setSearch] = useState('');
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    // Ein Suchtreffer (Listeneintrag) — oder nach einem geaenderten Plan der
    // frisch geladene Mutterstand (`loadProjectById`, siehe handleConfirm).
    const [selectedParent, setSelectedParent] = useState<ListProject | null>(null);
    const [billingMode, setBillingMode] = useState<'daily_flat' | 'individual'>('daily_flat');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * Wie viele eigene Toechter dieses Projekt fuehrt — `undefined` heisst
     * „wird noch geprueft", `null` heisst „nicht feststellbar".
     *
     * Ein Projekt mit eigenen Toechtern darf NICHT selbst Tochter werden. Sein
     * LV wanderte beim Verknuepfen an die neue Mutter, und die Buchungen
     * SEINER Toechter suchen die Position weiter bei ihm: useReportActions
     * bricht dann mit „Leistung im Mutterprojekt nicht gefunden." ab, und die
     * Begehung ist auf nichts gebucht. Der unbekannte Fall sperrt ebenfalls —
     * eine Verknuepfung laesst sich nachholen, eine Kette nicht ohne Weiteres
     * aufloesen.
     */
    const [ownChildren, setOwnChildren] = useState<number | null | undefined>(undefined);

    // Die eigene Projekt-id einmal aufgeloest: ProjectHub reicht sie als `id`
    // UND im Report weiter, und der Effekt unten braucht einen stabilen Wert
    // in seiner Abhaengigkeitsliste.
    const eigeneProjektId: string =
        currentProject?.data?.metadata?.globalData?.projectId || currentProject?.id || '';

    useEffect(() => {
        if (!isOpen) return;
        let abgebrochen = false;
        setOwnChildren(undefined);
        countLinkedChildren(eigeneProjektId)
            .then(n => { if (!abgebrochen) setOwnChildren(n); })
            .catch(() => { if (!abgebrochen) setOwnChildren(null); });
        // Aufgeraeumt wird ueber das Flag und nicht ueber ein Abbrechen der
        // Abfrage: eine spaet eintreffende Antwort auf einen geschlossenen
        // Dialog darf den Zustand nicht mehr anfassen.
        return () => { abgebrochen = true; };
    }, [isOpen, eigeneProjektId]);

    const chainBlock: string | null =
        ownChildren === undefined
            ? null
            : ownChildren === null
                ? 'Es liess sich nicht feststellen, ob dieses Projekt eigene Tochterprojekte hat. '
                  + 'Solange das offen ist, wird nicht verknüpft — eine Kette aus Mutter und Tochter '
                  + 'lässt Buchungen der Enkel ins Leere laufen.'
                : ownChildren > 0
                    ? `Dieses Projekt ist selbst Mutterprojekt von ${ownChildren} `
                      + `${ownChildren === 1 ? 'Tochterprojekt' : 'Tochterprojekten'}. Es kann deshalb nicht `
                      + 'selbst Tochter werden: sein Leistungsverzeichnis würde an die neue Mutter wandern, '
                      + 'und die Buchungen seiner Töchter fänden ihre Position nicht mehr. Bitte zuerst die '
                      + 'bestehenden Verknüpfungen auflösen.'
                    : null;

    // Was mit den eigenen Leistungen der Tochter geschieht. Wird angezeigt,
    // BEVOR etwas passiert — ohne diesen Hinweis blieben sie unbemerkt liegen:
    // ab dem Verknuepfen gilt das LV der Mutter, das eigene waere unsichtbar.
    const plan = useMemo<LinkTransferPlan>(
        () => planLinkTransfer(servicesOf(currentProject), servicesOf(selectedParent)),
        [currentProject, selectedParent],
    );

    const handleSearch = async () => {
        if (!search.trim()) return;
        setLoading(true);
        try {
            const projects = await loadProjectsFromCloud();
            // Filter: Not self, contains search term (null-sicher, siehe passtZurMutterSuche)
            const filtered = projects.filter(p =>
                p.id !== currentProject.id && passtZurMutterSuche(p, search)
            );
            setCandidates(filtered.map((p): Candidate => ({
                project: p,
                // Ein Projekt, das SELBST Tochter ist, darf keine Mutter sein:
                // es rechnet ueber ein fremdes LV ab und besitzt gar keines,
                // an das etwas wandern koennte.
                blockedReason: isChildProject(p)
                    ? 'ist selbst Tochterprojekt und kann keine Mutter sein'
                    : undefined,
            })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!selectedParent || busy || chainBlock) return;
        setError(null);
        setBusy(true);
        try {
            // ─── Die Pruefungen der Dialogoeffnung, FRISCH wiederholt ───────
            // Zwischen Suchen und Bestaetigen koennen Minuten liegen. In der
            // Zeit kann dieses Projekt selbst Mutter geworden sein, der
            // gewaehlte Treffer selbst Tochter — und das LV der Mutter kann
            // gewachsen sein, womit der angezeigte Plan eine Menge auf eine
            // Zeile schriebe, die inzwischen mehrdeutig oder anders bemessen
            // ist. Es entscheidet deshalb der Stand von JETZT.
            const [eigeneToechter, frischeMutter] = await Promise.all([
                countLinkedChildren(eigeneProjektId),
                loadProjectById(selectedParent.id, undefined, { skipMigration: true }),
            ]);
            // Der Zustand traegt die Meldung: chainBlock uebernimmt bei
            // Toechtern (oder „nicht feststellbar") wieder das Sperren.
            setOwnChildren(eigeneToechter);
            if (eigeneToechter === null || (eigeneToechter || 0) > 0) return;
            if (!frischeMutter || !frischeMutter.data) {
                setError('Das Mutterprojekt ließ sich nicht mehr laden. Es wurde nichts verknüpft.');
                return;
            }
            if (isChildProject(frischeMutter)) {
                setError('Das gewählte Projekt ist inzwischen selbst Tochterprojekt und kann keine Mutter sein. Es wurde nichts verknüpft.');
                return;
            }
            const frischerPlan = planLinkTransfer(servicesOf(currentProject), servicesOf(frischeMutter));
            if (linkTransferPlanFingerprint(frischerPlan) !== linkTransferPlanFingerprint(plan)) {
                // Der neue Stand gehoert VOR die Augen des Anwenders, nicht
                // still in die Ausfuehrung — bestaetigt hat er einen anderen
                // Plan. Der frische Mutterstand rechnet die Anzeige neu.
                setSelectedParent(frischeMutter);
                setError('Das Leistungsverzeichnis des Mutterprojekts hat sich seit der Anzeige geändert. '
                    + 'Der Plan wurde neu berechnet — bitte prüfen und erneut bestätigen. Es wurde nichts verknüpft.');
                return;
            }
            // Erst uebertragen, dann schliessen: schlaegt das Umhaengen fehl
            // (z. B. fehlende Rechte auf ein fremdes Mutterprojekt), bleibt der
            // Dialog mit der Meldung stehen und es wird nichts verknuepft.
            // Weitergereicht wird der FRISCHE Mutterstand — auf ihm ist der
            // Plan gerechnet.
            await onLink(frischeMutter, billingMode, frischerPlan);
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Die Leistungen konnten nicht übertragen werden. Es wurde nichts verknüpft.');
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div data-glass-view="projekt-dialog" className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <LinkIcon className="w-5 h-5 text-blue-400" />
                        Projekt verknüpfen
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto">
                    <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100">
                        <p>Sie definieren <strong>{anzeigeNameAktuellesProjekt(currentProject)}</strong> als <strong>Tochterprojekt</strong>.</p>
                        <p className="mt-1 text-xs opacity-80">Leistung und Budget werden vom Mutterprojekt übernommen und dort zentral abgerechnet.</p>
                    </div>

                    {chainBlock && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex gap-2">
                            <Ban className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold">Verknüpfen nicht möglich</p>
                                <p className="mt-1 text-[12px] leading-relaxed">{chainBlock}</p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-3">
                        <label className="text-xs font-bold text-slate-500 uppercase block">Mutterprojekt suchen</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500"
                                placeholder="Name oder Nummer..."
                            />
                            <Button variant="secondary" onClick={handleSearch} disabled={loading}>Suchen</Button>
                        </div>

                        {candidates.length > 0 && (
                            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg mt-2">
                                {candidates.map(({ project: p, blockedReason }) => (
                                    <div
                                        key={p.id}
                                        onClick={() => { if (!blockedReason) setSelectedParent(p); }}
                                        className={`p-3 text-sm border-b last:border-0 flex justify-between items-center ${
                                            blockedReason
                                                ? 'cursor-not-allowed bg-slate-50 opacity-70'
                                                : `cursor-pointer hover:bg-slate-50 ${selectedParent?.id === p.id ? 'bg-blue-50 border-blue-200' : ''}`
                                        }`}
                                    >
                                        <div>
                                            <div className={`font-bold ${blockedReason ? 'text-slate-500' : 'text-slate-700'}`}>{p.name}</div>
                                            <div className="text-xs text-slate-500">{p.project_number}</div>
                                            {/* Der Grund steht am Treffer und nicht anstelle des Treffers:
                                                weggefiltert liesse er den Anwender weitersuchen. */}
                                            {blockedReason && (
                                                <div className="text-[11px] text-red-700 mt-0.5 flex items-center gap-1">
                                                    <Ban className="w-3 h-3 shrink-0" />
                                                    <span>Nicht wählbar — {blockedReason}</span>
                                                </div>
                                            )}
                                        </div>
                                        {!blockedReason && selectedParent?.id === p.id && <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedParent && plan.total > 0 && (
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                                    <ArrowRightLeft className="w-4 h-4 shrink-0" />
                                    {plan.total === 1
                                        ? 'Diese Leistung wird an das Mutterprojekt übertragen'
                                        : `Diese ${plan.total} Leistungen werden an das Mutterprojekt übertragen`}
                                </p>
                                <p className="text-[11px] text-amber-800/80 mt-1">
                                    Ein Tochterprojekt rechnet über das LV der Mutter ab. Eigene Positionen
                                    blieben sonst unsichtbar liegen.
                                </p>

                                <ul className="mt-3 space-y-2">
                                    {plan.merges.map(merge => (
                                        <li key={merge.service.id} className="text-xs text-amber-900 bg-white/70 rounded-lg p-2 border border-amber-100">
                                            <span className="font-bold">{serviceLabel(merge.service)}</span>
                                            <span className="block opacity-80 mt-0.5">
                                                wird mit der gleichen Position der Mutter zusammengeführt
                                                {' '}({formatAmount(merge.into.quantityContracted || 0)}
                                                {' + '}{formatAmount(merge.service.quantityContracted || 0)}
                                                {' = '}<strong>{formatAmount(merge.quantityContracted)} {merge.service.unit}</strong>)
                                            </span>
                                        </li>
                                    ))}
                                    {plan.moves.map(move => (
                                        <li key={move.service.id} className="text-xs text-amber-900 bg-white/70 rounded-lg p-2 border border-amber-100">
                                            <span className="font-bold">{serviceLabel(move.service)}</span>
                                            <span className="block opacity-80 mt-0.5">
                                                {!move.reason && (
                                                    <>wandert unverändert mit ({formatAmount(move.service.quantityUsed || 0)} von{' '}
                                                    {formatAmount(move.service.quantityContracted || 0)} {move.service.unit} gebucht)</>
                                                )}
                                                {move.reason === 'history' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    darauf wurde bereits gebucht oder abgerechnet. Sie wandert als eigene Position,
                                                    damit Berichte und Rechnungen ihren Bezug behalten.</>
                                                )}
                                                {move.reason === 'ambiguous' && (
                                                    <>gibt es im Mutterprojekt <strong>{formatAmount(move.collisionCount || 2)}-mal</strong> mit
                                                    derselben LV-Nummer. Welche dieser Zeilen die Menge aufnehmen müsste, ist nicht
                                                    bestimmt — deshalb wird <strong>nicht</strong> zusammengeführt und nicht geraten.
                                                    Sie wandert als eigene Position; die Zeilen im Mutterprojekt bitte anschließend
                                                    von Hand ordnen.</>
                                                )}
                                                {move.reason === 'flat_rate' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    an ihr hängt eine <strong>Monatspauschale</strong>. Zusammengeführt wäre die
                                                    Deckungsregel weg, und die gedeckten Positionen ständen wieder als offenes Geld da.
                                                    Sie wandert als eigene Position.</>
                                                )}
                                                {move.reason === 'flat_covered' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    sie ist von einer <strong>Monatspauschale gedeckt</strong>. Zusammengeführt verlöre
                                                    die Pauschale ihr Ziel, und die gedeckte Menge stände wieder als offenes Geld da.
                                                    Sie wandert als eigene Position.</>
                                                )}
                                                {move.reason === 'waiver' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    auf ihr ist ein <strong>Verzicht</strong> vermerkt (samt Grund, Zeitpunkt und Person).
                                                    Sie wandert als eigene Position, damit dieser Nachweis erhalten bleibt.</>
                                                )}
                                                {move.reason === 'bestandteile' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    eine der beiden ist eine <strong>Paketposition mit Bestandteilen</strong>.
                                                    Zusammengeführt gingen die erledigten Bestandteile samt Datum, Uhrzeit und Person
                                                    verloren. Sie wandert als eigene Position, ihre Bestandteile ziehen mit um.</>
                                                )}
                                                {move.reason === 'price' && (
                                                    <>gibt es auch im Mutterprojekt, wird aber <strong>nicht</strong> zusammengeführt:
                                                    abweichender Einheitspreis
                                                    {canSeeFinancials
                                                        ? <> ({formatPrice(move.service.unitPrice)} statt{' '}
                                                          {formatPrice(move.collidesWith?.unitPrice || 0)})</>
                                                        : null}. Sie wandert als eigene Position.</>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {selectedParent && (
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                            <label className="text-xs font-bold text-slate-500 uppercase block">Abrechnungs-Modus</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setBillingMode('daily_flat')}
                                    className={`p-3 rounded-xl border text-left transition-all ${billingMode === 'daily_flat' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                                >
                                    <span className="block font-bold text-sm text-slate-800">Tagespauschale</span>
                                    <span className="block text-[11px] text-slate-500 mt-1">1x pro Tag für alle Projekte (Mutter & Töchter) zusammen.</span>
                                </button>
                                <button
                                    onClick={() => setBillingMode('individual')}
                                    className={`p-3 rounded-xl border text-left transition-all ${billingMode === 'individual' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                                >
                                    <span className="block font-bold text-sm text-slate-800">Einzeln</span>
                                    <span className="block text-[11px] text-slate-500 mt-1">Jede Begehung zählt separat vom Budget ab.</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleConfirm} disabled={!selectedParent || busy || !!chainBlock || ownChildren === undefined}>
                        {busy
                            ? 'Wird übertragen…'
                            : plan.total > 0 ? 'Übertragen & verknüpfen' : 'Verknüpfen'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
